import { useCallback, useMemo, useState } from 'react'

import { ButtonRoot } from '@/components/common/Button'
import { CardRec } from '@/components/common/Card'
import { Chart } from '@/components/common/Chart'
import { FloatingAnchored, FloatingHover, FloatingModal } from '@/components/common/Floating'
import { LinkOutbound } from '@/components/common/Link'
import { LoadingMask } from '@/components/common/LoadingMask'
import { Icon } from '@/components/icons/Icon'
import { useBreakpoints } from '@/components/providers/BreakpointProvider'
import { usePage } from '@/components/providers/PageProvider'
import { type CardIdType } from '@/defs/card'
import { useClickaway } from '@/hooks/useClickaway'
import { useInView } from '@/hooks/useInView'
import { type ChartLineType, getTrendingCardsOverTimeHelpers } from '@/utils/chartUtil'
import { getClickawayIgnoreProps, isClickawayIgnored } from '@/utils/clickawayUtil'
import { createDynamicComponent } from '@/utils/factoryUtil'

import styles from './HomeAboutChart.module.scss'

const HomeAboutChartGraph = createDynamicComponent({
  loader: () => import('./HomeAboutChartGraph/HomeAboutChartGraph'),
  name: 'HomeAboutChartGraph',
})

const HomeAboutChartTooltip = ({ activeLine }: { activeLine?: ChartLineType }) =>
  activeLine && (
    <div className={styles.tooltip}>
      <div className={styles.tooltipText}>
        <p className={styles.tooltipName}>{activeLine.rec.name}</p>
        <p className={styles.tooltipValue}>{activeLine.value}</p>
      </div>
      <CardRec forceLoad rec={activeLine.rec} />
    </div>
  )

export const HomeAboutChart = () => {
  const { isDesktop } = useBreakpoints()
  const { isInView, ref } = useInView()
  const { page } = usePage()
  const trendingCardsOverTime = page?.home?.trendingCardsOverTime
  const [activeId, setActiveId] = useState<string | null>(null)
  const isOpen = !!activeId

  const close = useCallback(() => setActiveId(null), [setActiveId])

  const onClick = useCallback(
    (cardId: CardIdType) => setActiveId(activeId === cardId ? null : cardId),
    [activeId, setActiveId],
  )

  const trendingCardsOverTimeHelpers = useMemo(
    () => getTrendingCardsOverTimeHelpers({ trendingCardsOverTime }),
    [trendingCardsOverTime],
  )
  const { lines, points } = trendingCardsOverTimeHelpers
  const activeLine = useMemo(() => lines.find((line) => line.rec.cardId === activeId), [activeId, lines])

  useClickaway({ ignore: 'chart', onClickAway: () => setActiveId(null) })

  const setIsOpen = useCallback((isOpen: boolean) => !isOpen && close(), [close])

  if (points.length < 1) return null

  return (
    <div className={styles.container}>
      {isDesktop ? (
        <div className={styles.popoverAnchor}>
          <FloatingAnchored
            content={<HomeAboutChartTooltip activeLine={activeLine} />}
            ignoreScopes={['chart']}
            isOpen={isOpen}
            maxHeight='available'
            onInteractOutside={(e) => {
              const target = e.detail.originalEvent.target
              if (target instanceof Element && isClickawayIgnored(target, 'chart')) e.preventDefault()
            }}
            padding='none'
            placement='left'
            setIsOpen={setIsOpen}
            variant='nobox'
          />
        </div>
      ) : (
        <FloatingModal
          content={<HomeAboutChartTooltip activeLine={activeLine} />}
          isFirst
          isOpen={isOpen}
          isTop
          setIsOpen={setIsOpen}
          variant='nobox'
        />
      )}
      <h2 className={styles.heading}>Trending Cards</h2>
      <p className={styles.subheading}>
        Last 8 weeks, by percent inclusion change{' '}
        <FloatingHover
          content={
            <p>
              Top 10 most-increased cards by percent inclusion change among{' '}
              <LinkOutbound href='/faq#potential-decks'>potential decks</LinkOutbound>. Avoids color fixing lands and
              mana rocks, and decklists that are similar to precons.
            </p>
          }
          styled
        >
          <ButtonRoot aria-label='Read More about trending cards' className={styles.infoIcon}>
            <Icon name='circleInfo' />
          </ButtonRoot>
        </FloatingHover>
      </p>
      <p className={styles.yAxisLabel}>Percent Change</p>
      <div className={styles.chart} ref={ref}>
        {isInView && (
          <HomeAboutChartGraph
            activeId={activeId}
            onClick={onClick}
            trendingCardsOverTimeHelpers={trendingCardsOverTimeHelpers}
          />
        )}
        <LoadingMask bgNone loading={!isInView} showShimmer={false} showSpinner />
      </div>
      <p className={styles.xAxisLabel}>Week</p>
      <Chart.Legend>
        {lines.map((line, i) => {
          const isActive = activeId === line.rec.cardId
          const isDimmed = activeId !== null && !isActive

          return (
            <Chart.LegendItem
              color={`var(--card-${line.number}-stroke, #fff)`}
              isActive={isActive}
              isDimmed={isDimmed}
              key={i}
              label={line.rec.name}
              onClick={() => onClick(line.rec.cardId)}
              value={line.value}
              {...getClickawayIgnoreProps(['chart'])}
            />
          )
        })}
      </Chart.Legend>
    </div>
  )
}
